import React from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useEncryptionStore } from '../../src/stores/encryptionStore';
import { useThemedStyles } from '../../src/theme/useThemedStyles';
import type { Theme } from '../../src/theme/types';

export default function EncryptionCompleteScreen() {
  const router = useRouter();
  const clearSensitiveData = useEncryptionStore((s) => s.clearSensitiveData);
  const styles = useThemedStyles(createStyles);

  const handleContinue = () => {
    clearSensitiveData();
    useEncryptionStore.getState().setE2EEComplete(true);
    router.replace('/(tabs)/activities');
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <View style={styles.iconContainer}>
          <Text style={styles.iconText}>{'\u{1F389}'}</Text>
        </View>

        <Text style={styles.title}>You're All Set!</Text>
        <Text style={styles.subtitle}>
          Your account is now protected with end-to-end encryption.
        </Text>

        <View style={styles.checklistContainer}>
          <View style={styles.checkRow}>
            <Text style={styles.checkIcon}>{'\u2713'}</Text>
            <Text style={styles.checkText}>Encryption key generated</Text>
          </View>
          <View style={styles.checkRow}>
            <Text style={styles.checkIcon}>{'\u2713'}</Text>
            <Text style={styles.checkText}>Key stored securely on device</Text>
          </View>
        </View>

        <View style={styles.warningBox}>
          <Text style={styles.warningText}>
            <Text style={styles.warningBold}>Keep your saved recovery phrase safe! </Text>
            It's the only way to recover your data if you lose access to your devices.
          </Text>
        </View>

        <TouchableOpacity
          style={styles.button}
          onPress={handleContinue}
          activeOpacity={0.7}
          accessibilityLabel="Start using the app"
          accessibilityRole="button"
          testID="complete.start-button"
        >
          <Text style={styles.buttonText}>Start Using App</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const createStyles = (theme: Theme) => ({
  container: { flex: 1 as const, justifyContent: 'center' as const, alignItems: 'center' as const, backgroundColor: theme.colors.brandedBackground, paddingHorizontal: 24 },
  card: { width: '100%' as const, maxWidth: 400, backgroundColor: theme.colors.surface, borderRadius: 16, padding: 24, alignItems: 'center' as const, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.1, shadowRadius: 12, elevation: 5 },
  iconContainer: { width: 64, height: 64, borderRadius: 32, backgroundColor: theme.colors.successBg, justifyContent: 'center' as const, alignItems: 'center' as const, marginBottom: 16 },
  iconText: { fontSize: 32 },
  title: { fontSize: 20, fontWeight: '700' as const, color: theme.colors.textPrimary, marginBottom: 8 },
  subtitle: { fontSize: 14, color: theme.colors.textMuted, textAlign: 'center' as const, marginBottom: 20 },
  checklistContainer: { width: '100%' as const, backgroundColor: theme.colors.bgSecondary, borderRadius: 12, padding: 16, marginBottom: 16 },
  checkRow: { flexDirection: 'row' as const, alignItems: 'center' as const, marginBottom: 12 },
  checkIcon: { fontSize: 16, fontWeight: '700' as const, color: theme.colors.success, marginRight: 12 },
  checkText: { fontSize: 14, color: theme.colors.textSecondary },
  warningBox: { width: '100%' as const, backgroundColor: theme.colors.warningBg, borderWidth: 1, borderColor: theme.colors.warningBorder, borderRadius: 12, padding: 16, marginBottom: 20 },
  warningText: { fontSize: 13, color: theme.colors.warningText, lineHeight: 20 },
  warningBold: { fontWeight: '700' as const },
  button: { width: '100%' as const, backgroundColor: theme.colors.primary, borderRadius: 10, paddingVertical: 14, alignItems: 'center' as const, justifyContent: 'center' as const, minHeight: 48 },
  buttonText: { color: theme.colors.textOnPrimary, fontSize: 16, fontWeight: '700' as const },
});
